import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	notFound,
	Outlet,
	redirect,
	useChildMatches,
	useNavigate,
	useRouter,
} from "@tanstack/react-router";
import { CalendarIcon, MailIcon, MoreVerticalIcon, PhoneIcon, UserIcon } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "@/components/admin/Container";
import { deleteCollectionItemModal } from "@/components/admin/DeleteModal";
import { formModal } from "@/components/admin/FormModal";
import { Header } from "@/components/admin/Header";
import { Searchbar } from "@/components/admin/Searchbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFile } from "@/hooks/use-file";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { formatDate } from "@/lib/dates";
import { capitalize, getFullName } from "@/lib/utils";
import { InvoiceForm } from "@/modules/invoices/form";
import { PatientForm } from "@/modules/patients/form";
import { patientsQueries } from "@/modules/patients/queries";
import { Collections } from "@/types/pocketbase.ts";

export const Route = createFileRoute("/dashboard/patients/$patientId")({
	component: RouteComponent,
	beforeLoad: ({ location, params }) => {
		const pathnames = location.pathname.split("/");
		const currentTab = pathnames[4];

		if (!currentTab) {
			throw redirect({
				to: "/dashboard/patients/$patientId/procedures",
				params: { patientId: params.patientId },
			});
		}
	},
	loader: async ({ context, params }) => {
		const queryClient = context.queryClient;

		const patient = await queryClient.ensureQueryData(patientsQueries.one({ id: params.patientId }));

		if (!patient) {
			throw notFound();
		}
	},
});

function RouteComponent() {
	const { t } = useTranslation();
	const { patientId } = Route.useParams();
	const patientQuery = useQuery(patientsQueries.one({ id: patientId }));
	const patient = patientQuery.data;

	return (
		<>
			<Header
				breadcrumbs={[
					{
						label: capitalize(t(($) => $.modules.dashboard.title)),
						href: "/dashboard",
					},
					{
						label: capitalize(t(($) => $.modules.patients.title)),
						href: "/dashboard/patients",
					},
					{
						label: getFullName(patient),
						href: `/dashboard/patients/${patientId}`,
					},
				]}
			>
				<div className="ml-auto flex items-center space-x-4">
					<Searchbar />
				</div>
			</Header>
			<Container className="mx-auto w-full max-w-6xl space-y-4">
				<PatientCard />

				<PageTabs />

				<Outlet />
			</Container>
		</>
	);
}

const PageTabs = () => {
	const { t } = useTranslation();
	const { patientId } = Route.useParams();
	const childMatches = useChildMatches();
	const firstChildMatch = childMatches[0];
	const pathname = firstChildMatch?.pathname || "";
	const navigate = useNavigate();

	const currentTab = useMemo(() => {
		if (pathname.includes("procedures")) {
			return "procedures";
		}

		if (pathname.includes("appointments")) {
			return "appointments";
		}

		if (pathname.includes("medical-history")) {
			return "medical-history";
		}

		if (pathname.includes("documents")) {
			return "documents";
		}

		return "procedures"; // Default tab
	}, [pathname]);

	const onTabChange = useCallback(
		(value: string) => {
			navigate({
				to: `/dashboard/patients/$patientId/${value}`,
				params: { patientId },
			});
		},
		[navigate, patientId]
	);

	return (
		<ScrollArea className="max-w-[calc(100vw-2rem)]">
			<Tabs value={currentTab}>
				<TabsList className="w-full">
					<TabsTrigger className="cursor-pointer" onClick={() => onTabChange("procedures")} value="procedures">
						{t(($) => $.modules.patients.tabs.procedures)}
					</TabsTrigger>
					<TabsTrigger className="cursor-pointer" onClick={() => onTabChange("appointments")} value="appointments">
						{t(($) => $.modules.patients.tabs.appointments)}
					</TabsTrigger>
					<TabsTrigger
						className="cursor-pointer"
						onClick={() => onTabChange("medical-history")}
						value="medical-history"
					>
						{t(($) => $.modules.patients.tabs.medicalHistory)}
					</TabsTrigger>
					<TabsTrigger className="cursor-pointer" onClick={() => onTabChange("documents")} value="documents">
						{t(($) => $.modules.patients.tabs.documents)}
					</TabsTrigger>
				</TabsList>
			</Tabs>
			<ScrollBar orientation="horizontal" />
		</ScrollArea>
	);
};

const PatientCard = () => {
	const { t } = useTranslation();
	const { patientId } = Route.useParams();
	const patientQuery = useQuery(patientsQueries.one({ id: patientId }));
	const patient = patientQuery.data;
	const file = useFile();
	const photoUrl = file.getUrl({
		collectionName: "Patients",
		id: patient?.id,
		filename: patient?.photo,
	});

	const dateFormat = {
		year: "numeric",
		month: "long",
		day: "numeric",
	} satisfies Intl.DateTimeFormatOptions;

	const formattedBornDate = patient?.bornDate ? formatDate(patient.bornDate, dateFormat) : "N/A";

	const formattedCreatedAt = patient?.created ? formatDate(patient.created, dateFormat) : "N/A";

	const { isBlocked, showBlockedModal } = useSubscriptionStatus();
	const router = useRouter();

	const onEdit = useCallback(() => {
		if (isBlocked) {
			showBlockedModal();
			return;
		}
		formModal.open({
			title: t(($) => $.modules.patients.forms.edit.title),
			description: t(($) => $.modules.patients.forms.edit.description),
			form: <PatientForm id={patientId} onCancel={formModal.close} onSuccess={formModal.close} />,
		});
	}, [patientId, t, isBlocked, showBlockedModal]);

	const onDelete = useCallback(() => {
		if (isBlocked) {
			showBlockedModal();
			return;
		}
		deleteCollectionItemModal.open({
			items: [{ collection: Collections.Patients, id: patientId }],
			onSuccess: () => {
				formModal.close();
				router.navigate({ to: "/dashboard/patients" });
			},
		});
	}, [patientId, isBlocked, showBlockedModal, router]);

	const onCreateInvoice = useCallback(() => {
		if (isBlocked) {
			showBlockedModal();
			return;
		}

		formModal.open({
			title: t(($) => $.modules.invoices.forms.create.title),
			description: t(($) => $.modules.invoices.forms.create.description),
			form: <InvoiceForm onCancel={formModal.close} onSuccess={formModal.close} patientId={patientId} />,
		});
	}, [isBlocked, t, patientId, showBlockedModal]);

	return (
		<Card className="">
			<CardContent className="">
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
					{/* Patient Image */}
					<div className="my-auto lg:col-span-2">
						<div className="relative flex aspect-square max-h-48 items-center justify-center overflow-hidden rounded-xl border bg-white">
							{photoUrl ? (
								<img alt={getFullName(patient)} className="object-cover" height={192} src={photoUrl} width={192} />
							) : (
								<div className="flex h-full items-center justify-center">
									<UserIcon className="h-12 w-12 text-gray-400" />
								</div>
							)}
						</div>
					</div>

					{/* Patient Details */}
					<div className="lg:col-span-10">
						<div className="flex h-full flex-col">
							<div className="mb-4 flex flex-col items-start justify-between gap-2 md:flex-row">
								<div>
									<h1 className="mb-1 font-bold text-2xl text-gray-900 dark:text-gray-100">{getFullName(patient)}</h1>
									<p className="text-gray-500 text-sm">{t(($) => $.modules.patients.details.subtitle)}</p>
								</div>

								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button size={"icon"} variant={"outline"}>
											<MoreVerticalIcon className="size-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={onEdit}>{t(($) => $.common.actions.edit)}</DropdownMenuItem>

										<DropdownMenuItem onClick={onCreateInvoice}>
											{t(($) => $.modules.invoices.forms.create.title)}
										</DropdownMenuItem>

										<DropdownMenuItem onClick={onDelete} variant="destructive">
											{t(($) => $.common.actions.delete)}
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>

							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
										<MailIcon className="h-5 w-5 text-primary" />
									</div>
									<div>
										<p className="font-medium text-gray-500 text-sm">{t(($) => $.modules.patients.details.email)}</p>
										<p className="text-gray-900 text-sm dark:text-gray-100">{patient?.email}</p>
									</div>
								</div>

								{patient?.phoneNumber && (
									<div className="flex items-center gap-3">
										<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
											<PhoneIcon className="h-5 w-5 text-primary" />
										</div>
										<div>
											<p className="font-medium text-gray-500 text-sm">{t(($) => $.modules.patients.details.phone)}</p>
											<p className="text-gray-900 text-sm dark:text-gray-100">{patient.phoneNumber}</p>
										</div>
									</div>
								)}

								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
										<CalendarIcon className="h-5 w-5 text-primary" />
									</div>
									<div>
										<p className="font-medium text-gray-500 text-sm">
											{t(($) => $.modules.patients.details.birthDate)}
										</p>
										<p className="text-gray-900 text-sm dark:text-gray-100">{formattedBornDate}</p>
									</div>
								</div>

								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
										<CalendarIcon className="h-5 w-5 text-primary" />
									</div>
									<div>
										<p className="font-medium text-gray-500 text-sm">
											{t(($) => $.modules.patients.details.createdAt)}
										</p>
										<p className="text-gray-900 text-sm dark:text-gray-100">{formattedCreatedAt}</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
};
